import React, { useState, useEffect } from 'react';
import { X, User, LogOut, ChevronDown, ChevronRight, MoreHorizontal } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';

export default function MobileSidebar({
  isOpen,
  onClose,
  menuItems = [],
  moreMenuItems = [],
  activeMenu,
  setActiveMenu,
  onLogout,
  userName = 'User',
  userRole = 'Role',
  portalType = 'admin',
}) {
  const [isMoreExpanded, setIsMoreExpanded] = useState(false);

  useEffect(() => {
    if (moreMenuItems.some(item => item.key === activeMenu)) {
      setIsMoreExpanded(true);
    }
  }, [activeMenu, moreMenuItems]);

  const accentConfig = {
    admin: { color: '#FF6B8A', bg: 'bg-pink-500/15', text: 'text-[#FF6B8A]', badge: 'bg-[#FF6B8A]', gradient: 'from-[#FF6B8A] to-[#E8366D]' },
    employee: { color: '#F59E0B', bg: 'bg-amber-500/15', text: 'text-amber-400', badge: 'bg-amber-500', gradient: 'from-amber-500 to-amber-600' },
    client: { color: '#3B82F6', bg: 'bg-blue-500/15', text: 'text-blue-400', badge: 'bg-blue-500', gradient: 'from-blue-500 to-blue-600' },
  };
  const accent = accentConfig[portalType] || accentConfig.admin;

  const handleMenuClick = (key, onClick) => {
    if (onClick) { onClick(); } else { setActiveMenu(key); }
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        side="left" 
        className="w-[280px] p-0 bg-[#111111] border-white/10"
      >
        <SheetHeader className="p-4 border-b border-white/[0.06]">
          <SheetTitle className="text-white flex items-center gap-2">
            <div className="flex items-center gap-2">
              <img src="/hed-icon.png" alt="Half Eaten Donut" className="w-5 h-5 rounded-lg" />
              <img src="/hed-logo-white.png" alt="Half Eaten Donut" className="h-5" />
              <div className="w-px h-4 bg-white/10 mx-1"></div>
              <img src="/girgit-logo.png" alt="Girgit" className="w-4 h-4 rounded-lg" />
              <span className="text-sm">Girgit</span>
            </div>
          </SheetTitle>
          <span className={`text-xs ${accent.badge} text-white px-2 py-0.5 rounded-full w-fit capitalize`}>
            {portalType}
          </span>
        </SheetHeader>

        {/* User Info */}
        <div className="p-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${accent.gradient} flex items-center justify-center`}>
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-semibold text-sm text-white">{userName}</p>
              <p className="text-xs text-neutral-500">{userRole}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 overflow-y-auto max-h-[calc(100vh-220px)]">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeMenu === item.key;
            return (
              <button
                key={item.key}
                onClick={() => handleMenuClick(item.key, item.onClick)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl mb-1 transition-all ${
                  isActive 
                    ? `${accent.bg} ${accent.text} font-semibold` 
                    : 'text-white/60 hover:bg-white/[0.06] hover:text-white'
                }`}
                data-testid={`mobile-menu-${item.key}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            );
          })}

          {/* More Section */}
          {moreMenuItems.length > 0 && (
            <div className="mt-2 pt-2 border-t border-white/[0.06]">
              <button
                onClick={() => setIsMoreExpanded(!isMoreExpanded)}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl mb-1 transition-all ${
                  moreMenuItems.some(item => item.key === activeMenu)
                    ? accent.text
                    : 'text-neutral-500 hover:bg-white/[0.06] hover:text-white'
                }`}
                data-testid="mobile-more-toggle"
              >
                <div className="flex items-center gap-3">
                  <MoreHorizontal className="w-5 h-5" />
                  <span className="text-sm font-medium">More</span>
                </div>
                {isMoreExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>

              {isMoreExpanded && (
                <div className="ml-2 space-y-1">
                  {moreMenuItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeMenu === item.key;
                    return (
                      <button
                        key={item.key}
                        onClick={() => handleMenuClick(item.key, item.onClick)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-2xl transition-all ${
                          isActive 
                            ? `${accent.bg} ${accent.text} font-semibold` 
                            : 'text-white/60 hover:bg-white/[0.06] hover:text-white'
                        }`}
                        data-testid={`mobile-menu-${item.key}`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm font-medium">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/[0.06] bg-[#111111]">
          <button
            onClick={() => { onLogout(); onClose(); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-white/40 hover:bg-white/[0.06] hover:text-[#FF6B8A] rounded-2xl transition-all"
            data-testid="mobile-logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-sm font-medium">Logout</span>
          </button>
          <p className="text-xs text-neutral-600 text-center mt-3">&copy; {new Date().getFullYear()} Half Eaten Donut</p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
